import { useCallback, useContext } from 'react';

import type {
	ClearReferrerCallback,
	RefreshSearchSessionIdCallback,
	SetQueryParamsCallback,
	SetThirdPartyConfigsCallback,
} from '../../../../../common/constants/bootstrap';
import { areAllEqualQueryParams, toQueryParamsRaw } from '../../../../../common/utils/query-params';
import { DynamicBootstrapContext, StaticBootstrapContext } from '../index';

/**
 * Modfies the current query params by calling the host product callback with the new query params.
 *
 * Modifies state indirectly, as the host product will update the URL which will cause the ueryParams in DynamicBootstrapContext to change
 */
export const useSetQueryParamsContext = (): SetQueryParamsCallback => {
	const { queryParams: currentQueryParams } = useContext(DynamicBootstrapContext);
	const { queryParamsCallback: hostProductSetQueryParamsCallback } =
		useContext(StaticBootstrapContext);

	const setQueryParamsCallback: SetQueryParamsCallback = useCallback(
		(newParams) => {
			// Performance optiomisation: don't do any work if the new param won't lead to a param change
			const areEqualQueryParams = areAllEqualQueryParams(newParams, currentQueryParams ?? {});
			if (areEqualQueryParams) {
				return;
			}

			// Transform into the raw format expected by the host product callback
			const rawQueryParams = toQueryParamsRaw(newParams);

			// Call the host product callback to update the URL with the new query params
			hostProductSetQueryParamsCallback(rawQueryParams);
		},
		[currentQueryParams, hostProductSetQueryParamsCallback],
	);

	return setQueryParamsCallback;
};

/**
 * Refresh the search session ID by calling the host product callback, modifies the store.
 */
export const useRefreshSearchSessionIdContext = (): RefreshSearchSessionIdCallback => {
	const { refreshSearchSessionId } = useContext(StaticBootstrapContext);
	return refreshSearchSessionId;
};

/**
 * Clear the referrer by calling the host product callback, modifies the store.
 */
export const useClearReferrerContext = (): ClearReferrerCallback => {
	const { clearReferrer } = useContext(StaticBootstrapContext);
	return clearReferrer;
};

/**
 * Set third party configs by calling the host product callback, modifies the store.
 */
export const useSetThirdPartyConfigsContext = (): SetThirdPartyConfigsCallback => {
	const { setThirdPartyConfigs } = useContext(StaticBootstrapContext);
	return setThirdPartyConfigs;
};
